
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { updatePlatformAdmin } from '@/services/platformAdminsService';
import { updateOrganizationMember } from '@/services/organizationUsersService';
import { User } from '@/types/user';

interface DeactivateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onUserUpdated: () => void;
}

export function DeactivateUserDialog({ open, onOpenChange, user, onUserUpdated }: DeactivateUserDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const isActive = user?.status === 'Active';

  const handleConfirm = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // Toggle the user's active status in the appropriate table based on user type
      if (user.user_type === 'platform_admin') {
        await updatePlatformAdmin(user.id, { is_active: !isActive });
      } else {
        await updateOrganizationMember(user.id, { is_active: !isActive });
      }

      toast({
        title: `User ${isActive ? 'deactivated' : 'activated'} successfully`,
        description: `${user.name} has been ${isActive ? 'deactivated' : 'activated'}.`,
      });

      onUserUpdated();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating user status:', error);
      setError(error.message || `Failed to ${isActive ? 'deactivate' : 'activate'} user`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isActive ? 'Deactivate' : 'Activate'} User
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to {isActive ? 'deactivate' : 'activate'} {user?.name}? 
            {isActive 
              ? ' They will no longer be able to access the system.' 
              : ' They will be able to access the system again.'
            }
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            variant={isActive ? "destructive" : "default"}
            onClick={handleConfirm} 
            disabled={loading}
          >
            {loading ? `${isActive ? 'Deactivating' : 'Activating'}...` : `${isActive ? 'Deactivate' : 'Activate'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
